/**
 * EmbryoReportScreen
 *
 * The Embryo testing page, one batch of embryos at a time or all of them. It
 * shows results, not advice:
 * the hero states how many embryos were tested and what came back, the lab
 * card shows how the eggs became tested embryos, the list carries every embryo
 * from the cycle with the reason behind its result and where it is now, and
 * the page ends with what the results mean, what the patient can do next and
 * where the report came from.
 *
 * Data: the report from `GET /cycles/:cycleId/embryo-report`, its versions
 * from `/history`, each embryo's vault record from the patient's specimens,
 * and the lab counts from the cycle's phases.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Linking,
  Pressable,
  ScrollView,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import {
  Dna,
  FilePlus,
  FileText,
  Pencil,
  Plus,
  type LucideIcon,
  BookOpen,
  Sparkles,
  Upload,
  CalendarDays,
  Clock,
  Layers,
  List,
  LayoutGrid,
  Table2,
  SlidersHorizontal,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { NavigationProp } from "@react-navigation/native";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { useSmartBack } from "../hooks/useSmartBack";
import { EMBER } from "../../onboarding/theme";
import type { CareStackParamList, MainTabParamList } from "../types";
import type { RootStackParamList } from "../../App";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  ActionBanner,
  ActionRowCard,
  CardTitleText,
  ClinicalDataStatus,
  SectionTitleRow,
  SectionActionLink,
  type SectionTitleRowAction,
  MoonFilterChip,
  CompactIconTabs,
  IconButton,
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
  InlineOptionPicker,
  CardSubtitleText,
  DocumentRow,
  EmptyState,
  CompactActionButton,
  DialogIcon,
  Row,
  SecondaryButton,
  StatusBadge,
  BackLink,
  cardSurface,
  LoadingState,
  useToast,
} from "@louise/moon-ui";
import { SCREEN_PADDING_TOP, colors } from "@louise/moon-ui/theme/tokens";
import {
  Hero,
  type HeroMenuAction,
  type HeroMetric,
} from "@src/packages/homepage/plan/components/hero/Hero";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { parseLocalDate } from "@src/packages/utils/date";
import embryoReportEmptyIllustration from "../../assets/images/embryo-genetics.png";
import type { Specimen } from "@src/packages/care/cryovault/useSpecimens";
import { OutcomeDotFunnelRows } from "@src/packages/homepage/plan/components/OutcomeDotFunnelRows";
import {
  EMBRYO_RESULTS,
  EMBRYO_RESULT_LABEL_KEYS,
  EMBRYO_RESULT_TONES,
  countFrozenEuploidEmbryos,
  describeEmbryoResultReason,
  describeEmbryoStorage,
  formatEmbryoDate,
  getCycleEmbryoEntries,
  getEmbryoLabStages,
  getEmbryoResultChanges,
  getEmbryoResultReason,
  getEmbryoTestingOverview,
  getFertilizationTechnique,
  useEmbryoSpecimens,
  useEmbryoTestingCycle,
  useEmbryoTestingReports,
  getEmbryoBatchDate,
  sortReportsByBatchDate,
  type EmbryoEntry,
  type EmbryoLabStage,
  type EmbryoResult,
  type EmbryoStorageState,
  describeEmbryoEntryName,
  describeEmbryoEntryStatus,
  type EmbryoTestingCycle,
  type EmbryoTestingOverview,
} from "@src/packages/care/cryovault/embryo-detail";
import {
  useEmbryoReport,
  useEmbryoReportHistory,
  useCreateEmbryoReport,
  useUpdateEmbryoReport,
  type EmbryoReport,
  type EmbryoReportHistoryEntry,
} from "@src/packages/cycles/hooks/useEmbryoReport";
import {
  CYCLE_TYPE_LABEL_KEYS,
  useCycles,
  type Cycle,
} from "@src/packages/cycles/hooks/useCycles";
import { formatCycleStart } from "@src/packages/cycles/formatCycleStart";
import { useGetDownloadUrl } from "@src/packages/documents/hooks/useDocuments";
import { EmbryoReportEntryForm } from "./EmbryoReportEntryForm";
import EmbryoDetailSheet from "../components/EmbryoDetailSheet";
import EmbryoComparisonTable from "../components/EmbryoComparisonTable";
import { DocumentRow as ScreenDocumentRow } from "./documents/DocumentRow";
import { formatDate } from "@src/packages/i18n/formatters";
import {
  toEmbryoReportCreateInput,
  toEmbryoReportFormValues,
  toEmbryoReportUpdateInput,
  type EmbryoReportFormValues,
} from "./embryoReportEntryInput";
import ScreenHeader from "../components/ScreenHeader";
import { useDocumentUploadController } from "../DocumentUploadController";
import { useStartTreatmentController } from "../StartTreatmentController";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = NativeStackScreenProps<CareStackParamList, "EmbryoReport">;

/** An embryo on the page, with the report it came from. */
type PageEntry = EmbryoEntry & { report: EmbryoReport };

const ALL_BATCHES = "all";

const PGT_ARTICLE_ID = "moon-article-pgt-testing";
const GENETIC_COUNSELING_PROCEDURE_ID = "proc-genetic-counseling";

const REPORT_STATE_TONES = {
  pending: "gold",
  partial: "gold",
  final: "green",
  retracted: "coral",
} as const;

const CARD_STYLE = {
  ...cardSurface(),
  padding: 15,
  gap: 12,
};

/** A short document date, e.g. "Aug 14", as the Care document rows show. */
function formatShortDate(
  value: string | null | undefined,
  locale: Parameters<typeof formatDate>[1],
) {
  if (!value) return undefined;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? parseLocalDate(value)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return formatDate(date, locale, { month: "short", day: "numeric" });
}

/** What Louise is asked when there are no results to summarise. */
const ASK_LOUISE_PROMPT_KEYS: Record<
  Exclude<EmbryoTestingOverview["state"], "results">,
  string
> = {
  awaiting_results:
    "care.embryo_report.next_steps.ask_louise_while_results_pending_prompt",
  withdrawn:
    "care.embryo_report.next_steps.ask_louise_about_withdrawn_report_prompt",
  not_tested: "care.embryo_report.next_steps.ask_louise_about_testing_prompt",
};

type EmbryoView = "list" | "cards" | "compare";

const EMBRYO_VIEWS: {
  value: EmbryoView;
  labelKey: string;
  icon: LucideIcon;
  action: string;
}[] = [
  {
    value: "list",
    labelKey: "care.embryo_report.compare.list_view",
    icon: List,
    action: "show_embryo_list",
  },
  {
    value: "cards",
    labelKey: "care.embryo_report.compare.cards_view",
    icon: LayoutGrid,
    action: "show_embryo_cards",
  },
  {
    value: "compare",
    labelKey: "care.embryo_report.compare.compare_view",
    icon: Table2,
    action: "show_embryo_comparison",
  },
];

/** A section heading as the Care screens space it. */
function PageSection({
  title,
  icon,
  action,
  right,
  children,
}: {
  title: string;
  icon: LucideIcon;
  action?: SectionTitleRowAction;
  /** Custom header controls, such as a view toggle. Replaces `action`. */
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <View style={{ marginTop: 28 }}>
      <SectionTitleRow
        title={title}
        icon={icon}
        action={action}
        right={right}
      />
      {children}
    </View>
  );
}

const LAB_STAGE_LABEL_KEYS: Record<EmbryoLabStage["id"], string> = {
  retrieved: "care.overview.lab_cryo.funnel_retrieved",
  mature: "care.overview.lab_cryo.funnel_mature",
  injected: "care.overview.lab_cryo.funnel_injected",
  fertilized: "care.overview.lab_cryo.funnel_fertilized",
  day3: "care.overview.lab_cryo.funnel_day3",
  blastocysts: "care.overview.lab_cryo.funnel_blastocysts",
  tested: "care.embryo_report.lab_journey.biopsied_and_tested",
  euploid: "care.embryo_report.lab_journey.euploid",
};

function getReportStateLabel(
  t: TranslationFunction,
  state: EmbryoReport["reportState"],
): string {
  switch (state) {
    case "pending":
      return t("care.embryo_report.report_state_pending");
    case "partial":
      return t("care.embryo_report.report_state_partial");
    case "final":
      return t("care.embryo_report.report_state_final");
    case "retracted":
      return t("care.embryo_report.report_state_retracted");
  }
}

function getTestTypeLabel(
  t: TranslationFunction,
  testType: EmbryoReport["testType"],
): string {
  const keyByTestType: Record<EmbryoReport["testType"], string> = {
    not_tested: "care.embryo_report.entry.test_type_not_tested",
    undecided: "care.embryo_report.entry.test_type_undecided",
    clinic_pending: "care.embryo_report.entry.test_type_clinic_pending",
    "PGT-A": "care.embryo_report.entry.test_type_pgt_a",
    "PGT-M": "care.embryo_report.entry.test_type_pgt_m",
    "PGT-SR": "care.embryo_report.entry.test_type_pgt_sr",
    "PGT-A + PGT-M": "care.embryo_report.entry.test_type_pgt_a_pgt_m",
    "PGT-A + PGT-SR": "care.embryo_report.entry.test_type_pgt_a_pgt_sr",
    "PGT-A + PGT-M + PGT-SR":
      "care.embryo_report.entry.test_type_pgt_a_pgt_m_pgt_sr",
    "Whole Genome Sequencing": "care.embryo_report.entry.test_type_wgs",
    other: "care.embryo_report.entry.test_type_other",
  };
  return t(keyByTestType[testType]);
}

function describeTransferOutcome(
  outcome: Cycle["outcome"] | undefined,
  t: TranslationFunction,
): string | null {
  if (!outcome) return null;
  if (
    outcome.pregnancyOutcome === "live_birth" ||
    outcome.pregnancyOutcome === "multiple_live_birth"
  ) {
    return t("care.embryo_report.transfer_outcome.live_birth");
  }
  switch (outcome.type) {
    case "pregnancy":
      return t("care.embryo_report.transfer_outcome.pregnancy");
    case "no_pregnancy":
      return t("care.embryo_report.transfer_outcome.no_pregnancy");
    case "miscarriage":
      return t("care.embryo_report.transfer_outcome.pregnancy_loss");
    default:
      return null;
  }
}

/** Where an embryo is now, with its straw so the patient can match the clinic. */
function describeWhereEmbryoIs(
  storage: EmbryoStorageState | null,
  cycles: readonly Cycle[],
  t: TranslationFunction,
  locale: string,
): string | null {
  if (!storage) return null;
  const where = describeEmbryoStorage(storage, t, locale);
  if (storage.kind === "frozen" && storage.location.straw) {
    return t("care.embryo_report.storage_with_straw", {
      params: { storage: where, straw: storage.location.straw },
    });
  }
  if (storage.kind === "transferred" && storage.cycleId) {
    const outcome = describeTransferOutcome(
      cycles.find((cycle) => cycle.id === storage.cycleId)?.outcome,
      t,
    );
    return outcome
      ? t("care.embryo_report.transfer_with_outcome", {
          params: { transfer: where, outcome },
        })
      : where;
  }
  return where;
}

function describeDevelopment(
  grade: string | null | undefined,
  day: number | null | undefined,
  t: TranslationFunction,
): string | null {
  if (grade && day != null) {
    return t("care.embryo_report.grade_day", {
      params: { grade, day: String(day) },
    });
  }
  if (grade) return grade;
  if (day != null) {
    return t("care.embryo_report.day_label", { params: { day: String(day) } });
  }
  return null;
}

// ── Page header ──

function EmbryoReportPageHeader({
  onBack,
  t,
}: {
  onBack: () => void;
  t: TranslationFunction;
}) {
  return (
    <>
      <BackLink
        label={t("care.overview.screen_title")}
        action="care.overview.screen_title"
        onPress={onBack}
        style={{ marginBottom: 8 }}
      />
      <ScreenHeader
        title={t("care.embryo_report.embryo_testing")}
        subtitle={t(
          "care.embryo_report.embryos_genetic_results_and_lab_reports",
        )}
      />
    </>
  );
}

// ── Hero ──

function embryosTestedTitle(
  overview: EmbryoTestingOverview,
  t: TranslationFunction,
): string {
  return overview.tested === 1
    ? t("care.embryo_report.hero.one_embryo_tested")
    : t(
        overview.isPartial
          ? "care.embryo_report.hero.embryos_tested_so_far"
          : "care.embryo_report.hero.embryos_tested",
        { params: { count: String(overview.tested) } },
      );
}

function EmbryoTestingHero({
  report,
  allReports,
  latestBatchDate,
  overview,
  menuActions,
  t,
  locale,
}: {
  /** The selected batch's report, or null when all are shown. */
  report: EmbryoReport | null;
  allReports: EmbryoReport[];
  /** When the newest batch's embryos were created. */
  latestBatchDate: string | null;
  overview: EmbryoTestingOverview;
  menuActions: HeroMenuAction[];
  t: TranslationFunction;
  locale: string;
}) {
  const metrics: HeroMetric[] =
    overview.state === "results"
      ? [
          ...EMBRYO_RESULTS.map((result) => ({
            label: t(EMBRYO_RESULT_LABEL_KEYS[result]),
            count: overview.counts[result],
          })),
          {
            label: t("care.embryo_report.not_tested"),
            count: overview.untested,
          },
        ]
          .filter((category) => category.count > 0)
          .map((category) => ({
            label: category.label,
            value: String(category.count),
            accessibilityLabel: `${category.label}: ${category.count}`,
          }))
      : [];

  if (!report) {
    const latest = formatEmbryoDate(latestBatchDate, locale);
    return (
      <Hero
        eyebrow={{
          label: t("care.embryo_report.batches.all_embryo_testing"),
          icon: Dna,
        }}
        title={embryosTestedTitle(overview, t)}
        summary={t("care.embryo_report.batches.across_batches", {
          params: { count: String(allReports.length), date: latest ?? "" },
        })}
        metrics={metrics.length > 0 ? metrics : undefined}
        metricVariant="tags"
        menuActions={menuActions.length > 0 ? menuActions : undefined}
        style={{ marginBottom: 12 }}
      />
    );
  }

  const reportDate = formatEmbryoDate(report.reportDate, locale);
  const biopsyDate = formatEmbryoDate(report.biopsyDate, locale);
  const eyebrow = [getTestTypeLabel(t, report.testType), report.laboratory]
    .filter(Boolean)
    .join(" · ");

  let title: string;
  let summary: string;
  switch (overview.state) {
    case "withdrawn":
      title = t("care.embryo_report.hero.report_was_withdrawn");
      summary = t("care.embryo_report.hero.do_not_rely_on_withdrawn_results");
      break;
    case "awaiting_results":
      title = t("care.embryo_report.hero.results_on_the_way");
      summary = biopsyDate
        ? t("care.embryo_report.hero.biopsied_on_date_results_pending", {
            params: { date: biopsyDate },
          })
        : t("care.embryo_report.hero.results_appear_when_lab_sends_them");
      break;
    case "not_tested":
      title = t("care.embryo_report.hero.no_genetic_testing_recorded");
      summary = t(
        report.testType === "clinic_pending"
          ? "care.embryo_report.review_clinic_pending"
          : "care.embryo_report.review_no_testing",
      );
      break;
    case "results": {
      title = embryosTestedTitle(overview, t);
      summary = reportDate
        ? t(
            overview.isPartial
              ? "care.embryo_report.hero.partial_report_from_date"
              : "care.embryo_report.hero.final_report_from_date",
            { params: { date: reportDate } },
          )
        : "";
      break;
    }
  }

  return (
    <Hero
      eyebrow={{ label: eyebrow, icon: Dna }}
      title={title}
      summary={summary}
      metrics={metrics.length > 0 ? metrics : undefined}
      metricVariant="tags"
      menuActions={menuActions.length > 0 ? menuActions : undefined}
      style={{ marginBottom: 12 }}
    />
  );
}

// ── Retrievals ──

function BatchChips({
  reports,
  batchDateOf,
  reportlessCycle = null,
  selection,
  onSelect,
  style,
  t,
  locale,
}: {
  reports: EmbryoReport[];
  batchDateOf: (report: EmbryoReport) => string | null;
  /** A cycle being shown without a report yet, so the row can say so. */
  reportlessCycle?: { key: string; label: string } | null;
  selection: string;
  onSelect: (selection: string) => void;
  style?: StyleProp<ViewStyle>;
  t: TranslationFunction;
  locale: string;
}) {
  const options: { key: string; label: string; icon: LucideIcon }[] = [
    ...reports.map((report, index) => ({
      key: report.cycleId,
      label:
        index === 0
          ? t("care.embryo_report.batches.latest_batch")
          : (formatEmbryoDate(batchDateOf(report), locale) ??
            t("care.embryo_report.batches.earlier_batch")),
      icon: index === 0 ? Clock : CalendarDays,
    })),
    ...(reportlessCycle
      ? [
          {
            key: reportlessCycle.key,
            label: reportlessCycle.label,
            icon: FilePlus,
          },
        ]
      : []),
    ...(reports.length > 1
      ? [
          {
            key: ALL_BATCHES,
            label: t("care.embryo_report.batches.all_batches"),
            icon: Layers,
          },
        ]
      : []),
  ];
  if (options.length < 2) return null;
  return (
    <BatchChipRow
      options={options}
      selection={selection}
      onSelect={onSelect}
      style={style}
    />
  );
}

/**
 * The batch chips as a scrolling row that keeps the selected chip in view, for
 * example a cycle chosen from "Upload a report" that sits at the row's end.
 */
function BatchChipRow({
  options,
  selection,
  onSelect,
  style,
}: {
  options: { key: string; label: string; icon: LucideIcon }[];
  selection: string;
  onSelect: (selection: string) => void;
  style?: StyleProp<ViewStyle>;
}) {
  const scrollRef = useRef<ScrollView>(null);
  const chips = useRef(new Map<string, { x: number; width: number }>());
  const viewport = useRef({ width: 0, scrollX: 0 });
  const [laidOut, setLaidOut] = useState(0);
  useEffect(() => {
    const chip = chips.current.get(selection);
    const { width, scrollX } = viewport.current;
    if (!chip || width === 0) return;
    const fullyVisible =
      chip.x >= scrollX && chip.x + chip.width <= scrollX + width;
    if (fullyVisible) return;
    scrollRef.current?.scrollTo({
      x: Math.max(0, chip.x - 16),
      animated: true,
    });
  }, [laidOut, selection]);
  // The hero leaves 12 below it, so 16 more puts the row 28 from the hero,
  // the same gap the next section opens with.
  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={16}
      onLayout={(event) => {
        viewport.current.width = event.nativeEvent.layout.width;
        setLaidOut((count) => count + 1);
      }}
      onScroll={(event) => {
        viewport.current.scrollX = event.nativeEvent.contentOffset.x;
      }}
      style={[{ marginHorizontal: -16, marginTop: 16 }, style]}
      contentContainerStyle={{
        gap: 8,
        paddingLeft: 16,
        paddingRight: 24,
        paddingBottom: 2,
      }}
    >
      {options.map((option) => (
        <View
          key={option.key}
          onLayout={(event) => {
            const { x, width } = event.nativeEvent.layout;
            chips.current.set(option.key, { x, width });
            setLaidOut((count) => count + 1);
          }}
        >
          <MoonFilterChip
            label={option.label}
            icon={option.icon}
            selected={selection === option.key}
            onPress={() => onSelect(option.key)}
            tone="glass"
            showCheck={false}
            action="select_embryo_testing_batch"
          />
        </View>
      ))}
    </ScrollView>
  );
}

// ── In the lab ──

function LabJourneyCard({
  stages,
  t,
}: {
  stages: EmbryoLabStage[];
  t: TranslationFunction;
}) {
  const retrieved = stages[0]?.value ?? 1;
  return (
    <View style={{ ...CARD_STYLE, marginTop: 28 }}>
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
        <View
          style={{
            width: 38,
            height: 38,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <OrganicMiniIcon kind="panel" size={36} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <CardTitleText>
            {t("care.overview.lab_cryo.laboratory_activity_title")}
          </CardTitleText>
          <CardSubtitleText>
            {t("care.overview.lab_cryo.laboratory_activity_subtitle")}
          </CardSubtitleText>
        </View>
      </View>
      <OutcomeDotFunnelRows
        embedded
        totalDots={Math.max(retrieved, 1)}
        stages={stages.map((stage) => ({
          id: stage.id,
          label: t(LAB_STAGE_LABEL_KEYS[stage.id]),
          value: stage.value,
          detail:
            stage.shareOfRetrieved == null
              ? t("care.overview.lab_cryo.funnel_detail_recorded")
              : t("care.overview.lab_cryo.funnel_detail_share", {
                  params: { share: String(stage.shareOfRetrieved) },
                }),
        }))}
      />
    </View>
  );
}

// ── Embryos ──

function EmbryoCollection({
  view,
  entries,
  specimens,
  cycles,
  showBatch,
  batchDateOf,
  hideResults,
  onSelect,
  t,
  locale,
}: {
  view: "list" | "cards";
  entries: PageEntry[];
  specimens: readonly Specimen[];
  cycles: readonly Cycle[];
  /** When several batches are listed, say which one each embryo is from. */
  showBatch: boolean;
  batchDateOf: (report: EmbryoReport) => string | null;
  /** A withdrawn report's results must not read as facts. */
  hideResults: boolean;
  onSelect: (entry: PageEntry) => void;
  t: TranslationFunction;
  locale: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const capturePress = usePressCapture("EmbryoCollection");
  const visibleEntries =
    view === "cards" && !expanded ? entries.slice(0, 4) : entries;
  const items = visibleEntries.map((entry) => {
    const isTested = entry.kind === "tested";
    const name = describeEmbryoEntryName(entry, t);
    const reason =
      isTested && !hideResults ? getEmbryoResultReason(entry.embryo) : null;
    const findings =
      isTested && !hideResults
        ? entry.embryo.monogenicFindings
            .filter((finding) => finding.status !== "unaffected")
            .map((finding) =>
              t(
                finding.status === "affected"
                  ? "care.embryo_report.row_finding.affected_by_condition"
                  : "care.embryo_report.row_finding.carrier_for_condition",
                { params: { condition: finding.condition } },
              ),
            )
            .join(" · ")
        : null;
    const batchDate = showBatch
      ? formatEmbryoDate(batchDateOf(entry.report), locale)
      : null;
    const development = isTested
      ? describeDevelopment(entry.embryo.grade, entry.embryo.biopsyDay, t)
      : describeDevelopment(entry.grade, entry.day, t);
    const detailLines = [
      batchDate
        ? t("care.embryo_report.batches.batch_from_date", {
            params: { date: batchDate },
          })
        : null,
      development,
      describeWhereEmbryoIs(entry.storage, cycles, t, locale),
    ].filter((part): part is string => Boolean(part));
    const status = describeEmbryoEntryStatus(entry, t);
    const badge = hideResults ? null : (
      <StatusBadge label={status.label} tone={status.tone} />
    );
    const summary = [
      reason ? describeEmbryoResultReason(reason, t) : null,
      findings,
    ].filter((line): line is string => Boolean(line));
    const action = isTested
      ? "open_embryo_detail"
      : entry.kind === "awaiting"
        ? "open_embryo_awaiting_result"
        : "open_untested_embryo";

    if (view === "cards") {
      const specimen = isTested
        ? specimens.find(
            (record) => record.id === entry.embryo.embryoSpecimenId,
          )
        : entry.specimen;
      const createdOn = formatEmbryoDate(
        specimen?.collectedAt?.slice(0, 10),
        locale,
      );
      const cardDetails = [
        createdOn
          ? t("care.embryo_report.embryo_created_on_date", {
              params: { date: createdOn },
            })
          : null,
        development,
      ].filter((part): part is string => Boolean(part));

      return (
        <Pressable
          key={entry.key}
          testID={`embryo-card-${entry.key}`}
          accessibilityRole="button"
          accessibilityLabel={name}
          onPress={() => {
            capturePress(pressLabel(name), action);
            onSelect(entry);
          }}
          style={({ pressed }) => ({
            ...cardSurface(),
            flex: 1,
            minWidth: 0,
            padding: 12,
            gap: 8,
            opacity: pressed ? 0.82 : 1,
          })}
        >
          <OrganicMiniIcon kind="genetics" size={34} />
          <View style={{ gap: 6, alignItems: "flex-start" }}>
            <CardTitleText>{name}</CardTitleText>
            {badge}
          </View>
          {summary.length > 0 && (
            <CardSubtitleText numberOfLines={3}>
              {summary.join(" · ")}
            </CardSubtitleText>
          )}
          <View style={{ marginTop: "auto" }}>
            {cardDetails.map((line, index) => (
              <CardSubtitleText key={index}>{line}</CardSubtitleText>
            ))}
          </View>
        </Pressable>
      );
    }

    return (
      <DocumentRow
        key={entry.key}
        leading={<OrganicMiniIcon kind="genetics" size={34} />}
        title={name}
        titleRight={badge}
        subtitle={[...summary, detailLines.join(" · ")]
          .filter((line): line is string => Boolean(line))
          .join(" · ")}
        subtitleNumberOfLines={3}
        onPress={() => onSelect(entry)}
        accessibilityLabel={name}
        action={action}
      />
    );
  });

  return (
    <View testID={`embryo-${view}`} style={{ gap: 10 }}>
      {view === "cards"
        ? Array.from({ length: Math.ceil(items.length / 2) }, (_, index) => (
            <View
              key={index}
              style={{ flexDirection: "row", alignItems: "stretch", gap: 10 }}
            >
              {items.slice(index * 2, index * 2 + 2).map((item) => (
                <View key={item.key} style={{ flex: 1, minWidth: 0 }}>
                  {item}
                </View>
              ))}
              {index * 2 + 1 >= items.length && (
                <View style={{ flex: 1, minWidth: 0 }} />
              )}
            </View>
          ))
        : items}
      {view === "cards" && !expanded && entries.length > 4 && (
        <View style={{ alignItems: "flex-end" }}>
          <SectionActionLink
            label={t("care.view_all")}
            onPress={() => setExpanded(true)}
            action="show_all_embryo_cards"
            testID="view-all-embryo-cards"
          />
        </View>
      )}
    </View>
  );
}

// ── What the results mean ──

const RESULT_MEANING_KEYS: Record<EmbryoResult, string> = {
  euploid: "care.embryo_report.meaning.expected_chromosome_count",
  mosaic: "care.embryo_report.meaning.some_cells_differ",
  aneuploid: "care.embryo_report.meaning.extra_or_missing_chromosome",
  no_result: "care.embryo_report.meaning.not_enough_dna_for_result",
};

function ResultMeanings({
  overview,
  t,
}: {
  overview: EmbryoTestingOverview;
  t: TranslationFunction;
}) {
  const present = EMBRYO_RESULTS.filter((r) => overview.counts[r] > 0);
  return (
    <View style={CARD_STYLE}>
      {present.map((result) => (
        <View
          key={result}
          style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}
        >
          <StatusBadge
            label={t(EMBRYO_RESULT_LABEL_KEYS[result])}
            tone={EMBRYO_RESULT_TONES[result]}
          />
          <CardSubtitleText style={{ flex: 1 }}>
            {t(RESULT_MEANING_KEYS[result])}
          </CardSubtitleText>
        </View>
      ))}
      {overview.untested > 0 && (
        <View
          style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}
        >
          <StatusBadge label={t("care.embryo_report.not_tested")} />
          <CardSubtitleText style={{ flex: 1 }}>
            {t("care.embryo_report.meaning.frozen_without_biopsy")}
          </CardSubtitleText>
        </View>
      )}
    </View>
  );
}

// ── What you can do next ──

type NextStep = {
  key: string;
  icon: OrganicMiniIconKind;
  title: string;
  subtitle: string;
  onPress: () => void;
};

function NextSteps({ steps }: { steps: NextStep[] }) {
  if (steps.length === 0) return null;
  return (
    <View style={{ gap: 10 }}>
      {steps.map((step) => (
        <ActionRowCard
          key={step.key}
          leading={<OrganicMiniIcon kind={step.icon} size={36} />}
          title={step.title}
          subtitle={step.subtitle}
          onPress={step.onPress}
          action={step.key}
        />
      ))}
    </View>
  );
}

// ── Lab report ──

function LabReportRow({
  report,
  onOpen,
  t,
  locale,
}: {
  report: EmbryoReport;
  onOpen?: () => void;
  t: TranslationFunction;
  locale: Parameters<typeof formatDate>[1];
}) {
  return (
    <ScreenDocumentRow
      title={
        report.attachment?.upload?.name ?? t("care.embryo_report.lab_report")
      }
      subtitle={report.laboratory || undefined}
      dateLabel={formatShortDate(report.reportDate, locale)}
      icon={
        <FileText size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
      }
      onPress={onOpen}
    />
  );
}

function LabReportSection({
  report,
  history,
  onOpenLabReport,
  onAddLabReport,
  onAddReportFromAnotherCycle,
  t,
  locale,
}: {
  report: EmbryoReport;
  history: EmbryoReportHistoryEntry[];
  onOpenLabReport?: () => void;
  onAddLabReport?: () => void;
  onAddReportFromAnotherCycle?: () => void;
  t: TranslationFunction;
  locale: Parameters<typeof formatDate>[1];
}) {
  const upload = report.attachment?.upload;
  const versions = [...history].sort((a, b) => b.version - a.version);
  const showVersions = versions.length > 1;
  if (
    !upload &&
    !onAddLabReport &&
    !showVersions &&
    !onAddReportFromAnotherCycle
  ) {
    return null;
  }

  return (
    <PageSection title={t("care.embryo_report.lab_report")} icon={FileText}>
      <View testID="embryo-lab-report" style={{ gap: 8 }}>
        {upload && onOpenLabReport ? (
          <LabReportRow
            report={report}
            onOpen={onOpenLabReport}
            t={t}
            locale={locale}
          />
        ) : onAddLabReport ? (
          <ScreenDocumentRow
            title={t("care.embryo_report.add_lab_report")}
            subtitle={t("care.embryo_report.keep_lab_report_with_results")}
            icon={<OrganicMiniIcon kind="medical-records" size={34} />}
            trailing={
              <Plus
                size={18}
                color={EMBER.colors.textLight}
                strokeWidth={1.5}
              />
            }
            onPress={onAddLabReport}
            showChevron={false}
          />
        ) : null}
        {showVersions && (
          <View style={{ ...cardSurface(), overflow: "hidden" }}>
            {versions.map((entry, index) => {
              const previous = versions[index + 1];
              const changes = previous
                ? getEmbryoResultChanges(previous, entry)
                : [];
              return (
                <Row
                  key={entry.id}
                  surface="plain"
                  title={t("care.embryo_report.history.version_label", {
                    params: { version: String(entry.version) },
                  })}
                  titleRight={
                    <StatusBadge
                      label={getReportStateLabel(t, entry.reportState)}
                      tone={REPORT_STATE_TONES[entry.reportState]}
                    />
                  }
                  subtitle={[
                    t("care.embryo_report.history.updated_on", {
                      params: {
                        date: formatEmbryoDate(entry.reportDate, locale) ?? "",
                      },
                    }),
                    ...changes.map((change) => describeResultChange(change, t)),
                    entry.correctionReason
                      ? t(
                          "care.embryo_report.history.correction_reason_label",
                          {
                            params: { reason: entry.correctionReason },
                          },
                        )
                      : null,
                  ]
                    .filter((line): line is string => Boolean(line))
                    .join("\n")}
                />
              );
            })}
          </View>
        )}
        {onAddReportFromAnotherCycle ? (
          <AddReportFromAnotherCycleRow
            onPress={onAddReportFromAnotherCycle}
            t={t}
          />
        ) : null}
      </View>
    </PageSection>
  );
}

/** A cycle by its type and start, e.g. "IVF Cycle · Oct 6, 2025". */
function describeEmbryoTestingCycle(
  cycle: EmbryoTestingCycle,
  t: TranslationFunction,
  locale: string,
): string {
  const typeKey = CYCLE_TYPE_LABEL_KEYS[cycle.cycleType];
  const type = typeKey ? t(typeKey) : cycle.cycleType;
  const started = formatCycleStart(cycle.startDate, locale);
  return started ? `${type} · ${started}` : type;
}

/** Offers a report for a cycle that has none yet. */
function AddReportFromAnotherCycleRow({
  onPress,
  t,
}: {
  onPress: () => void;
  t: TranslationFunction;
}) {
  return (
    <ScreenDocumentRow
      title={t("care.embryo_report.add_report.add_a_report_from_another_cycle")}
      icon={<OrganicMiniIcon kind="medical-records" size={34} />}
      trailing={
        <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
      }
      onPress={onPress}
      showChevron={false}
    />
  );
}

/** Asks which cycle a new report belongs to. */
function AddReportDialog({
  visible,
  cycles,
  onClose,
  onContinue,
  t,
  locale,
}: {
  visible: boolean;
  cycles: EmbryoTestingCycle[];
  onClose: () => void;
  onContinue: (cycleId: string) => void;
  t: TranslationFunction;
  locale: string;
}) {
  const [chosenCycleId, setChosenCycleId] = useState(cycles[0]?.id ?? "");
  useEffect(() => {
    if (visible) setChosenCycleId(cycles[0]?.id ?? "");
  }, [cycles, visible]);
  const options = cycles.map((cycle) => ({
    id: cycle.id,
    label: describeEmbryoTestingCycle(cycle, t, locale),
    action: "choose_cycle_for_embryo_report",
  }));

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      contentStyle={{ paddingHorizontal: 20, paddingBottom: 18 }}
      testID="add-embryo-report-dialog"
    >
      <DialogHeader
        title={t(
          "care.embryo_report.add_report.which_cycle_is_the_report_from",
        )}
        style={{ marginBottom: 14 }}
      />
      <InlineOptionPicker
        options={options}
        value={chosenCycleId}
        onChange={setChosenCycleId}
        wrap
      />
      <DialogActions style={{ marginTop: 18 }}>
        <SecondaryButton
          label={t("care.embryo_report.add_report.cancel_adding_a_report")}
          onPress={onClose}
          flex={1}
        />
        <PrimaryButton
          label={t("care.embryo_report.add_report.continue_with_this_cycle")}
          onPress={() => onContinue(chosenCycleId)}
          disabled={!chosenCycleId}
          flex={1}
        />
      </DialogActions>
    </Dialog>
  );
}

/** Each batch's lab report, when every batch is shown. */
function AllLabReports({
  reports,
  onOpen,
  onAddReportFromAnotherCycle,
  t,
  locale,
}: {
  reports: EmbryoReport[];
  onOpen: (uploadId: string) => void;
  onAddReportFromAnotherCycle?: () => void;
  t: TranslationFunction;
  locale: Parameters<typeof formatDate>[1];
}) {
  const withDocuments = reports.filter((shown) => shown.attachment?.uploadId);
  if (withDocuments.length === 0 && !onAddReportFromAnotherCycle) return null;
  return (
    <PageSection title={t("care.embryo_report.lab_reports")} icon={FileText}>
      <View style={{ gap: 8 }}>
        {withDocuments.map((shown) => (
          <LabReportRow
            key={shown.id}
            report={shown}
            onOpen={() => {
              const uploadId = shown.attachment?.uploadId;
              if (uploadId) onOpen(uploadId);
            }}
            t={t}
            locale={locale}
          />
        ))}
        {onAddReportFromAnotherCycle ? (
          <AddReportFromAnotherCycleRow
            onPress={onAddReportFromAnotherCycle}
            t={t}
          />
        ) : null}
      </View>
    </PageSection>
  );
}

function describeResultChange(
  change: ReturnType<typeof getEmbryoResultChanges>[number],
  t: TranslationFunction,
): string {
  const embryo = t("care.embryo_report.embryo_number", {
    params: { number: String(change.embryoNumber) },
  });
  if (change.from && change.to) {
    return t("care.embryo_report.history.result_changed", {
      params: {
        embryo,
        from: t(EMBRYO_RESULT_LABEL_KEYS[change.from]),
        to: t(EMBRYO_RESULT_LABEL_KEYS[change.to]),
      },
    });
  }
  return t(
    change.to
      ? "care.embryo_report.history.embryo_added"
      : "care.embryo_report.history.embryo_removed",
    { params: { embryo } },
  );
}

// ── Main screen ──

export default function EmbryoReportScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const { locale } = useLocale();
  const { cycleId } = route.params;
  const [selection, setSelection] = useState<string>(cycleId);
  const activeCycleId = selection === ALL_BATCHES ? cycleId : selection;
  const [selectedEmbryoId, setSelectedEmbryoId] = useState<string | null>(null);
  const [embryoView, setEmbryoView] = useState<EmbryoView>("list");
  const [comparisonColumnsOpen, setComparisonColumnsOpen] = useState(false);
  const [comparisonColumnVisibility, setComparisonColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [entryMode, setEntryMode] = useState<"create" | "edit" | null>(null);
  const [pendingAttachmentId, setPendingAttachmentId] = useState<string | null>(
    null,
  );
  const [pendingAttachmentName, setPendingAttachmentName] = useState<
    string | null
  >(null);
  const { openDocumentUpload } = useDocumentUploadController();
  const { openStartTreatment } = useStartTreatmentController();
  const getDownloadUrl = useGetDownloadUrl();
  const { showToast } = useToast();
  const {
    specimens,
    error: specimensError,
    refetch: refetchSpecimens,
    isFetching: isFetchingSpecimens,
  } = useEmbryoSpecimens();
  const { data: cycles = [] } = useCycles();

  const {
    data: report,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useEmbryoReport(activeCycleId);
  const { data: reportHistory = [] } = useEmbryoReportHistory(
    report ? activeCycleId : undefined,
  );
  const { reports: unorderedReports, cyclesWithoutReport } =
    useEmbryoTestingReports(cycleId);
  // Newest batch first, dated by when the embryos were created.
  const allReports = useMemo(
    () => sortReportsByBatchDate(unorderedReports, specimens),
    [specimens, unorderedReports],
  );
  const batchDateOf = (shown: EmbryoReport) =>
    getEmbryoBatchDate(shown, specimens);
  const [isAddingReport, setIsAddingReport] = useState(false);
  const cycleWithoutReport =
    cyclesWithoutReport.find((cycle) => cycle.id === activeCycleId) ?? null;
  const openAddReport =
    cyclesWithoutReport.length > 0 ? () => setIsAddingReport(true) : undefined;
  const isAll = selection === ALL_BATCHES && allReports.length > 1;
  const createReport = useCreateEmbryoReport();
  const updateReport = useUpdateEmbryoReport();
  const { cycleType, phases } = useEmbryoTestingCycle(activeCycleId);

  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();

  const openArticle = (articleId: string) => {
    setSelectedEmbryoId(null);
    rootNavigation?.navigate("Learn", {
      screen: "ArticleDetail",
      params: { articleId, returnTab: "Care" },
    });
  };

  const askLouise = (prompt: string) => {
    setSelectedEmbryoId(null);
    rootNavigation?.navigate("Chat", {
      screen: "Assistant",
      params: { initialPrompt: prompt, autoSendInitialPrompt: false },
    });
  };

  const openCareTeam = () => {
    rootNavigation
      ?.getParent<NavigationProp<RootStackParamList>>()
      ?.navigate("Profile", {
        screen: "ConnectionsDetail",
        params: { cycleId: activeCycleId, initialKind: "provider" },
      });
  };

  const openGeneticCounseling = () => {
    rootNavigation?.navigate("Access", {
      screen: "CanonicalTestFactsheet",
      params: {
        procedureId: GENETIC_COUNSELING_PROCEDURE_ID,
        returnTab: "Care",
      },
    });
  };

  const planTransfer = (specimenIds: string[], labels: string[]) => {
    setSelectedEmbryoId(null);
    openStartTreatment({
      presetCycleType: "fet",
      frozenPrefill:
        specimenIds.length > 0
          ? { type: "embryo", specimenIds, specimenLabels: labels }
          : null,
      source: "EmbryoReportScreen",
      onCreated: (createdCycle) =>
        navigation.navigate("CycleDetail", { cycleId: createdCycle?.id }),
      onOpenActiveCycle: (cycle) =>
        navigation.navigate("CycleDetail", { cycleId: cycle.id }),
    });
  };

  const openSourceUpload = (mode: "create" | "edit") => {
    openDocumentUpload({
      defaultCategory: "medical_records",
      onSave: ({ fileName, uploadId }) => {
        if (!uploadId) return;
        setPendingAttachmentId(uploadId);
        setPendingAttachmentName(fileName || null);
        setEntryMode(mode);
      },
    });
  };

  const openSourceDocument = async (
    uploadId = report?.attachment?.uploadId,
  ) => {
    if (!uploadId) return;

    try {
      const url = await getDownloadUrl.mutateAsync({
        uploadId,
        disposition: "inline",
      });
      if (url) await Linking.openURL(url);
    } catch (error) {
      console.error("Failed to access embryo report source document", error);
      showToast({
        variant: "error",
        message: t("care.embryo_report.lab_report_couldnt_open"),
      });
    }
  };

  const handleReportSubmit = (values: EmbryoReportFormValues) => {
    if (!entryMode) return;

    const onSuccess = () => {
      setEntryMode(null);
      setPendingAttachmentId(null);
      setPendingAttachmentName(null);
      void refetch();
    };

    if (entryMode === "edit") {
      const data = toEmbryoReportUpdateInput(values);
      if (!data) return;
      updateReport.mutate({ cycleId: activeCycleId, data }, { onSuccess });
      return;
    }

    const data = toEmbryoReportCreateInput({
      ...values,
      attachmentId: pendingAttachmentId,
    });
    if (!data) return;
    createReport.mutate({ cycleId: activeCycleId, data }, { onSuccess });
  };

  const cancelReportEntry = () => {
    createReport.reset();
    updateReport.reset();
    setEntryMode(null);
    setPendingAttachmentId(null);
    setPendingAttachmentName(null);
  };

  const shownReports = useMemo(
    () =>
      isAll
        ? allReports.filter(
            (shown) =>
              shown.reportState === "final" || shown.reportState === "partial",
          )
        : report
          ? [report]
          : [],
    [allReports, isAll, report],
  );
  const entries = useMemo<PageEntry[]>(
    () =>
      shownReports.flatMap((shown) =>
        getCycleEmbryoEntries(shown, specimens).map((entry) => ({
          ...entry,
          report: shown,
        })),
      ),
    [shownReports, specimens],
  );
  const overview = useMemo(() => {
    const untested = entries.filter(
      (entry) => entry.kind === "untested",
    ).length;
    if (isAll) {
      return getEmbryoTestingOverview(
        {
          reportState: "final",
          testType: shownReports[0]?.testType ?? "PGT-A",
          embryos: shownReports.flatMap((shown) => shown.embryos),
        },
        untested,
      );
    }
    return report ? getEmbryoTestingOverview(report, untested) : null;
  }, [entries, isAll, report, shownReports]);
  const labStages = useMemo(
    () => (report ? getEmbryoLabStages(phases, cycleType, report) : []),
    [phases, cycleType, report],
  );
  const euploidBank = useMemo(
    () =>
      report
        ? countFrozenEuploidEmbryos(
            report,
            specimens,
            allReports.filter((other) => other.id !== report.id),
          )
        : null,
    [allReports, report, specimens],
  );
  const latestChanges = useMemo(() => {
    if (!report || report.version < 2) return [];
    const previous = reportHistory.find(
      (entry) => entry.version === report.version - 1,
    );
    return previous ? getEmbryoResultChanges(previous, report) : [];
  }, [report, reportHistory]);

  const selectedEntry =
    entries.find(
      (entry) =>
        entry.kind === "tested" && entry.embryo.id === selectedEmbryoId,
    ) ?? null;
  const selectedEmbryo =
    selectedEntry?.kind === "tested" ? selectedEntry.embryo : null;
  const selectedSpecimen =
    selectedEmbryo?.embryoSpecimenId != null
      ? (specimens.find((s) => s.id === selectedEmbryo.embryoSpecimenId) ??
        null)
      : null;

  const scrollContainerStyle = {
    paddingHorizontal: 16,
    paddingTop: SCREEN_PADDING_TOP,
    paddingBottom: 100,
    maxWidth: 520,
    width: "100%" as const,
    alignSelf: "center" as const,
  };

  if (!isAll && isLoading) {
    return (
      <GlassScreenLayout>
        <ScrollView contentContainerStyle={scrollContainerStyle}>
          <EmbryoReportPageHeader onBack={handleBack} t={t} />
          <LoadingState
            testID="screen-loading"
            style={{ paddingVertical: 60 }}
          />
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  if (!isAll && error) {
    return (
      <GlassScreenLayout>
        <ScrollView contentContainerStyle={scrollContainerStyle}>
          <EmbryoReportPageHeader onBack={handleBack} t={t} />
          <ClinicalDataStatus
            state="unavailable"
            title={t("clinical_data.unavailable_title")}
            message={t("clinical_data.unavailable_body")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={() => void refetch()}
            isRetrying={isFetching}
            testID="embryo-report-unavailable"
          />
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  if (!isAll && !report) {
    return (
      <GlassScreenLayout>
        <ScrollView contentContainerStyle={scrollContainerStyle}>
          <EmbryoReportPageHeader onBack={handleBack} t={t} />
          <BatchChips
            reports={allReports}
            batchDateOf={batchDateOf}
            reportlessCycle={
              cycleWithoutReport
                ? {
                    key: cycleWithoutReport.id,
                    label: describeEmbryoTestingCycle(
                      cycleWithoutReport,
                      t,
                      locale,
                    ),
                  }
                : null
            }
            selection={selection}
            onSelect={setSelection}
            style={{ marginTop: 0, marginBottom: 16 }}
            t={t}
            locale={locale}
          />
          {entryMode === "create" ? (
            <EmbryoReportEntryForm
              onCancel={cancelReportEntry}
              onSubmit={handleReportSubmit}
              isSubmitting={createReport.isPending}
              sourceDocumentName={pendingAttachmentName}
              errorMessage={
                createReport.error instanceof Error
                  ? createReport.error.message
                  : null
              }
            />
          ) : (
            <EmptyState
              testID="embryo-report-empty-state"
              density="compact"
              illustration={
                <DialogIcon
                  source={embryoReportEmptyIllustration}
                  width={68}
                  height={80}
                  testID="embryo-report-empty-illustration"
                />
              }
              title={t("care.embryo_report.empty_title")}
              titleVariant="featured"
              subtitle={t("care.embryo_report.empty_body")}
              style={{ paddingVertical: 28 }}
              action={
                <View
                  style={{
                    width: "100%",
                    maxWidth: 420,
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <CompactActionButton
                    label={t("care.embryo_report.upload_or_import_report")}
                    action="care.embryo_report.upload_or_import_report"
                    variant="primary"
                    onPress={() => openSourceUpload("create")}
                    flexGrow={1}
                    flexBasis={200}
                    leadingIcon={Upload}
                  />
                  <CompactActionButton
                    label={t("care.embryo_report.entry.open_form")}
                    action="care.embryo_report.entry.open_form"
                    variant="secondary"
                    onPress={() => setEntryMode("create")}
                    flexGrow={1}
                    flexBasis={200}
                    leadingIcon={Pencil}
                  />
                </View>
              }
            />
          )}
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  if (!isAll && report && entryMode === "edit") {
    return (
      <GlassScreenLayout>
        <ScrollView contentContainerStyle={scrollContainerStyle}>
          <EmbryoReportPageHeader onBack={handleBack} t={t} />
          <EmbryoReportEntryForm
            mode="edit"
            initialValues={{
              ...toEmbryoReportFormValues(report),
              attachmentId:
                pendingAttachmentId ??
                toEmbryoReportFormValues(report).attachmentId,
            }}
            onCancel={cancelReportEntry}
            onSubmit={handleReportSubmit}
            isSubmitting={updateReport.isPending}
            sourceDocumentName={pendingAttachmentName}
            errorMessage={
              updateReport.error instanceof Error
                ? updateReport.error.message
                : null
            }
          />
        </ScrollView>
      </GlassScreenLayout>
    );
  }

  const activeReport = isAll ? null : (report ?? null);
  const testingOverview =
    overview ??
    getEmbryoTestingOverview(
      { reportState: "pending", testType: "PGT-A", embryos: [] },
      0,
    );
  const isPatientOwned = activeReport?.ownership === "patient";
  const RESULT_ORDER: Record<EmbryoResult, number> = {
    euploid: 0,
    mosaic: 1,
    aneuploid: 2,
    no_result: 3,
  };
  const orderedEntries = [...entries].sort((a, b) => {
    const rank = (entry: PageEntry) =>
      entry.kind === "tested"
        ? RESULT_ORDER[entry.embryo.result]
        : entry.kind === "awaiting"
          ? 4
          : 5;
    return rank(a) - rank(b);
  });
  // A withdrawn report's results must not be laid side by side as facts.
  const canCompareEmbryos =
    orderedEntries.length > 1 && testingOverview.state !== "withdrawn";
  const activeEmbryoView =
    embryoView === "compare" && !canCompareEmbryos ? "list" : embryoView;
  const openEntry = (entry: PageEntry) => {
    if (entry.kind !== "tested") {
      navigation.navigate("CryoVault", { specimenId: entry.specimen.id });
    } else if (testingOverview.state !== "withdrawn") {
      setSelectedEmbryoId(entry.embryo.id);
    } else if (entry.embryo.embryoSpecimenId) {
      navigation.navigate("CryoVault", {
        specimenId: entry.embryo.embryoSpecimenId,
      });
    }
  };
  const frozenEntries = entries.filter(
    (entry) => entry.storage?.kind === "frozen",
  );
  const hasCounselingFindings =
    testingOverview.counts.mosaic > 0 ||
    testingOverview.counts.aneuploid > 0 ||
    shownReports.some((shown) =>
      shown.embryos.some((embryo) =>
        embryo.monogenicFindings.some((f) => f.status !== "unaffected"),
      ),
    );

  const uploadAnotherReport: HeroMenuAction[] = openAddReport
    ? [
        {
          icon: FilePlus,
          label: t("care.embryo_report.add_report.upload_a_report"),
          action: "upload_another_report",
          onPress: openAddReport,
        },
      ]
    : [];
  const menuActions: HeroMenuAction[] = !activeReport
    ? uploadAnotherReport
    : [
        ...(activeReport.attachment?.upload
          ? [
              {
                icon: FileText,
                label: t("care.embryo_report.open_lab_report"),
                action: "open_lab_report",
                onPress: () => void openSourceDocument(),
              },
            ]
          : []),
        ...(isPatientOwned
          ? [
              {
                icon: Upload,
                label: t(
                  activeReport.attachment?.upload
                    ? "care.embryo_report.replace_lab_report"
                    : "care.embryo_report.add_lab_report",
                ),
                action: activeReport.attachment?.upload
                  ? "replace_lab_report"
                  : "add_lab_report",
                onPress: () => openSourceUpload("edit"),
              },
              {
                icon: Pencil,
                label: t("care.embryo_report.entry.edit"),
                action: "edit_report",
                onPress: () => setEntryMode("edit"),
              },
            ]
          : []),
        ...uploadAnotherReport,
      ];

  // Results only reach Louise as facts when there are results to read; a
  // withdrawn report's counts must not.
  const resultsPrompt =
    testingOverview.state === "results"
      ? t("care.embryo_report.next_steps.ask_louise_prompt", {
          params: {
            summary: [
              getTestTypeLabel(t, shownReports[0]?.testType ?? "PGT-A"),
              ...EMBRYO_RESULTS.filter(
                (r) => testingOverview.counts[r] > 0,
              ).map(
                (r) =>
                  `${testingOverview.counts[r]} ${t(EMBRYO_RESULT_LABEL_KEYS[r])}`,
              ),
            ].join(", "),
          },
        })
      : t(ASK_LOUISE_PROMPT_KEYS[testingOverview.state]);

  const nextSteps: NextStep[] = [
    ...(testingOverview.state === "withdrawn"
      ? [
          {
            key: "contact_care_team_about_withdrawn_report",
            icon: "clinics" as const,
            title: t("care.embryo_report.next_steps.contact_your_care_team"),
            subtitle: t(
              "care.embryo_report.next_steps.ask_for_corrected_report",
            ),
            onPress: openCareTeam,
          },
        ]
      : []),
    ...(testingOverview.state === "results" && frozenEntries.length > 0
      ? [
          {
            key: "plan_frozen_embryo_transfer",
            icon: "genetics" as const,
            title: t("care.embryo_report.next_steps.plan_frozen_transfer"),
            subtitle: t(
              "care.embryo_report.next_steps.choose_embryo_with_clinic",
            ),
            onPress: () => planTransfer([], []),
          },
        ]
      : []),
    ...(testingOverview.hasNoTransferableResult ||
    testingOverview.hasOnlyNoResults
      ? [
          {
            key: "talk_to_doctor_about_next_options",
            icon: "clinics" as const,
            title: t("care.embryo_report.next_steps.talk_to_your_doctor"),
            subtitle: t(
              testingOverview.hasOnlyNoResults
                ? "care.embryo_report.next_steps.no_result_can_be_rebiopsied"
                : "care.embryo_report.next_steps.doctor_can_walk_through_options",
            ),
            onPress: openCareTeam,
          },
        ]
      : []),
    ...(testingOverview.state === "results" && hasCounselingFindings
      ? [
          {
            key: "learn_about_genetic_counseling",
            icon: "learn" as const,
            title: t("care.embryo_report.next_steps.talk_to_genetic_counselor"),
            subtitle: t(
              "care.embryo_report.next_steps.what_counseling_involves",
            ),
            onPress: openGeneticCounseling,
          },
        ]
      : []),
    {
      key: "ask_louise_about_embryo_results",
      icon: "concierge" as const,
      title: t(
        testingOverview.state === "results"
          ? "care.embryo_report.next_steps.ask_louise_about_results"
          : "care.embryo_report.next_steps.ask_louise_about_embryo_testing",
      ),
      subtitle: t("care.embryo_report.next_steps.questions_about_any_term"),
      onPress: () => askLouise(resultsPrompt),
    },
  ];

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={scrollContainerStyle}
        showsVerticalScrollIndicator={false}
      >
        <EmbryoReportPageHeader onBack={handleBack} t={t} />

        <EmbryoTestingHero
          report={activeReport}
          allReports={allReports}
          latestBatchDate={allReports[0] ? batchDateOf(allReports[0]) : null}
          overview={testingOverview}
          menuActions={menuActions}
          t={t}
          locale={locale}
        />

        {/* Without the vault, untested embryos, storage and the frozen count
            drop off this page; say so rather than let them vanish. */}
        {specimensError ? (
          <ClinicalDataStatus
            state="stale"
            message={t("care.embryo_report.vault_details_couldnt_load")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={() => void refetchSpecimens()}
            isRetrying={isFetchingSpecimens}
            style={{ marginTop: 16 }}
            testID="embryo-report-vault-stale"
          />
        ) : null}

        <BatchChips
          reports={allReports}
          batchDateOf={batchDateOf}
          selection={selection}
          onSelect={(next) => {
            setSelectedEmbryoId(null);
            setSelection(next);
          }}
          t={t}
          locale={locale}
        />

        {activeReport && latestChanges.length > 0 && (
          <View style={{ marginTop: 28 }}>
            <ActionBanner
              variant="warning"
              message={[
                t("care.embryo_report.history.report_was_corrected_on_date", {
                  params: {
                    date:
                      formatEmbryoDate(activeReport.reportDate, locale) ?? "",
                  },
                }),
                ...latestChanges.map((change) =>
                  describeResultChange(change, t),
                ),
              ].join(" ")}
            />
          </View>
        )}

        {orderedEntries.length > 0 && (
          <PageSection
            title={t("care.embryo_report.your_embryos")}
            icon={Dna}
            right={
              <View
                testID="embryo-comparison-controls"
                style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
              >
                <CompactIconTabs
                  options={EMBRYO_VIEWS.filter(
                    (view) => view.value !== "compare" || canCompareEmbryos,
                  ).map((view) => {
                    const label = t(view.labelKey);
                    return {
                      value: view.value,
                      label,
                      icon: view.icon,
                      action: view.action,
                      accessibilityLabel: t(
                        "care.embryo_report.compare.show_view",
                        { params: { label: label.toLowerCase() } },
                      ),
                    };
                  })}
                  value={activeEmbryoView}
                  onChange={(view) => {
                    setEmbryoView(view);
                    setComparisonColumnsOpen(false);
                  }}
                  compact
                />
                {activeEmbryoView === "compare" && (
                  <IconButton
                    size={32}
                    surface="frosted"
                    hitSlop={6}
                    accessibilityLabel={t(
                      "care.embryo_report.compare.edit_comparison_columns",
                    )}
                    action="customize_embryo_comparison_columns"
                    testID="edit-embryo-comparison-columns"
                    onPress={() => setComparisonColumnsOpen(true)}
                  >
                    <SlidersHorizontal
                      size={12}
                      color={colors.textMid}
                      strokeWidth={1.8}
                    />
                  </IconButton>
                )}
              </View>
            }
          >
            {activeEmbryoView === "compare" ? (
              <EmbryoComparisonTable
                customizing={comparisonColumnsOpen}
                onCloseCustomization={() => setComparisonColumnsOpen(false)}
                columnVisibility={comparisonColumnVisibility}
                onColumnVisibilityChange={setComparisonColumnVisibility}
                embryos={orderedEntries}
                showBatch={isAll}
                batchDateOf={batchDateOf}
                onSelect={openEntry}
                t={t}
                locale={locale}
              />
            ) : (
              <EmbryoCollection
                key={selection}
                view={activeEmbryoView === "cards" ? "cards" : "list"}
                entries={orderedEntries}
                specimens={specimens}
                cycles={cycles}
                showBatch={isAll}
                batchDateOf={batchDateOf}
                hideResults={testingOverview.state === "withdrawn"}
                onSelect={openEntry}
                t={t}
                locale={locale}
              />
            )}
          </PageSection>
        )}

        {activeReport &&
          testingOverview.state !== "withdrawn" &&
          euploidBank &&
          euploidBank.total > euploidBank.fromThisReport && (
            <View style={{ marginTop: 10 }}>
              <ActionRowCard
                leading={<OrganicMiniIcon kind="cryo" size={36} />}
                title={t(
                  euploidBank.fromThisReport === 0
                    ? "care.embryo_report.bank.none_from_this_batch"
                    : euploidBank.fromThisReport === 1
                      ? "care.embryo_report.bank.one_from_this_batch"
                      : "care.embryo_report.bank.some_from_this_batch",
                  {
                    params: {
                      count: String(euploidBank.fromThisReport),
                      total: String(euploidBank.total),
                    },
                  },
                )}
                subtitle={t("care.embryo_report.bank.see_all_in_cryo_vault")}
                onPress={() => navigation.navigate("CryoVault", {})}
                action="open_cryo_vault_from_embryo_bank"
              />
            </View>
          )}

        {testingOverview.state === "results" && (
          <PageSection
            title={t("care.embryo_report.meaning.what_these_results_mean")}
            icon={BookOpen}
            action={{
              label: t("care.embryo_report.meaning.learn_about_embryo_testing"),
              accessibilityLabel: t(
                "care.embryo_report.meaning.what_embryo_testing_checks",
              ),
              onPress: () => openArticle(PGT_ARTICLE_ID),
            }}
          >
            <ResultMeanings overview={testingOverview} t={t} />
          </PageSection>
        )}

        <PageSection
          title={t("care.embryo_report.next_steps.what_you_can_do_next")}
          icon={Sparkles}
        >
          <NextSteps steps={nextSteps} />
        </PageSection>

        {activeReport && labStages.length > 1 && (
          <LabJourneyCard stages={labStages} t={t} />
        )}

        {activeReport ? (
          <LabReportSection
            report={activeReport}
            history={reportHistory}
            onOpenLabReport={
              activeReport.attachment?.upload
                ? () => void openSourceDocument()
                : undefined
            }
            onAddLabReport={
              isPatientOwned && testingOverview.state !== "awaiting_results"
                ? () => openSourceUpload("edit")
                : undefined
            }
            onAddReportFromAnotherCycle={openAddReport}
            t={t}
            locale={locale}
          />
        ) : (
          <AllLabReports
            reports={shownReports}
            onOpen={(uploadId) => void openSourceDocument(uploadId)}
            onAddReportFromAnotherCycle={openAddReport}
            t={t}
            locale={locale}
          />
        )}

        <AddReportDialog
          visible={isAddingReport}
          cycles={cyclesWithoutReport}
          onClose={() => setIsAddingReport(false)}
          onContinue={(targetCycleId) => {
            setIsAddingReport(false);
            setSelectedEmbryoId(null);
            setEntryMode(null);
            setSelection(targetCycleId);
          }}
          t={t}
          locale={locale}
        />

        {selectedEntry && (
          <EmbryoDetailSheet
            embryo={selectedEmbryo}
            report={selectedEntry.report}
            specimen={selectedSpecimen}
            fertilizationTechnique={getFertilizationTechnique(
              phases,
              cycleType,
            )}
            transferOutcome={
              selectedEntry?.storage?.kind === "transferred"
                ? describeWhereEmbryoIs(
                    selectedEntry.storage,
                    cycles,
                    t,
                    locale,
                  )
                : null
            }
            onClose={() => setSelectedEmbryoId(null)}
            onOpenLabReport={
              selectedEntry.report.attachment?.upload
                ? () =>
                    void openSourceDocument(
                      selectedEntry.report.attachment?.uploadId,
                    )
                : undefined
            }
            onOpenInCryoVault={
              selectedSpecimen
                ? () => {
                    setSelectedEmbryoId(null);
                    navigation.navigate("CryoVault", {
                      specimenId: selectedSpecimen.id,
                    });
                  }
                : undefined
            }
            onPlanTransfer={
              selectedSpecimen && selectedEntry?.storage?.kind === "frozen"
                ? () =>
                    planTransfer(
                      [selectedSpecimen.id],
                      [
                        t("care.embryo_report.embryo_number", {
                          params: {
                            number: String(selectedEmbryo?.embryoNumber ?? ""),
                          },
                        }),
                      ],
                    )
                : undefined
            }
            onAskLouise={(prompt) => askLouise(prompt)}
            embryoChoices={orderedEntries.flatMap((entry) => {
              if (entry.kind !== "tested") return [];
              const name = t("care.embryo_report.embryo_number", {
                params: { number: String(entry.embryo.embryoNumber) },
              });
              const batchDate = isAll
                ? formatShortDate(batchDateOf(entry.report), locale)
                : undefined;
              return [
                {
                  id: entry.embryo.id,
                  label: batchDate ? `${name} · ${batchDate}` : name,
                  result: entry.embryo.result,
                  polygenicRisks: entry.embryo.polygenicRisks,
                },
              ];
            })}
            onSelectEmbryo={setSelectedEmbryoId}
          />
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
